import React, { useState } from "react";
import Checkbox from "@mui/material/Checkbox";
import FormControl from "@mui/material/FormControl";
import MenuItem from "@mui/material/MenuItem";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import { SxProps, Theme } from "@mui/material";

/**
 * Interface for options in the multi-select component.
 */
interface Option {
  id: number;
  label: string;
}

/**
 * Props interface for the MultiSelect component.
 */
interface MultiSelectProps {
  options: Option[];
  onSelectionChange: (selectedOptions: number[]) => void;
  selectedOptions: number[];
  dataTestId?: string;
  sx?: SxProps<Theme>;
}

const selectSx = {
  fontSize: 12,
  height: 30,
  minHeight: 30,
  boxSizing: "border-box",
  mt: "-4px",
  ".MuiSelect-select": {
    display: "flex",
    alignItems: "center",
    height: 30,
    paddingTop: "4px",
    paddingBottom: "4px",
    boxSizing: "border-box",
  },
};

/**
 * MultiSelect component to handle multiple selection of options.
 * @param {MultiSelectProps} props - The props for the component.
 * @returns {JSX.Element} MultiSelect component.
 */
const MultiSelect: React.FC<MultiSelectProps> = ({
  options,
  onSelectionChange,
  selectedOptions,
  dataTestId = "multi-select",
  sx,
}) => {
  const [isValid, setIsValid] = useState<boolean>(true);

  /**
   * Handles the change in selected options.
   * @param {SelectChangeEvent<number[]>} event - The change event.
   */
  const handleOptionChange = (event: SelectChangeEvent<number[]>) => {
    const newSelectedOptions = event.target.value as number[];
    onSelectionChange(newSelectedOptions);
    setIsValid(newSelectedOptions.length > 0);
  };

  const formControlRef = React.useRef<HTMLDivElement>(null);
  const [menuWidth, setMenuWidth] = useState<number | undefined>(undefined);

  return (
    <FormControl fullWidth error={!isValid} size="small" ref={formControlRef} sx={{ fontSize: 12, minWidth: 0, ml: 0 }}>
      <Select
        labelId="multi-select-label"
        id="multi-select"
        multiple
        value={selectedOptions}
        onChange={handleOptionChange}
        size="small"
        data-testid={dataTestId}
        renderValue={(selected) => (
          <div>
            {selected.map((value, index) => (
              <span key={value}>
                {options.find((option) => option.id === value)?.label}
                {index < selected.length - 1 && ", "}
              </span>
            ))}
          </div>
        )}
        MenuProps={{
          PaperProps: {
            sx: {
              fontSize: 12,
              minWidth: menuWidth,
              maxWidth: menuWidth,
            },
          },
        }}
        sx={selectSx}
        onOpen={() => {
          if (formControlRef.current) {
            const width = formControlRef.current.getBoundingClientRect().width;
            setMenuWidth(width);
          }
        }}
      >
        {options.map((option) => (
          <MenuItem key={option.id} value={option.id} sx={{ fontSize: 11, py: 0.25, minHeight: 0 }}>
            <Checkbox checked={selectedOptions.includes(option.id)} size="small" sx={{ p: 0.5 }} />
            {option.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export default MultiSelect;
